import { Component, ReactNode } from 'react'
import { Box, Text, Button, VStack, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react'
import { ErrorReportingService, fromError } from '../utils/errorHandling'

interface Props {
    children: ReactNode
    fallback?: ReactNode
    onError?: (error: Error) => void
}

interface State {
    hasError: boolean
    error?: Error
    errorId?: string
}

export default class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // 使用新的錯誤處理服務
        const frontendError = fromError(error)
        ErrorReportingService.getInstance().report(frontendError, {
            additionalInfo: {
                type: 'errorBoundary',
                errorInfo: {
                    componentStack: errorInfo.componentStack,
                    errorBoundary: errorInfo.componentStack
                }
            }
        })

        // 調用父組件的錯誤處理器
        if (this.props.onError) {
            this.props.onError(error)
        }

        console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            const error = this.state.error
            const isDevelopment = process.env.NODE_ENV === 'development'

            return (
                <Box p={8} textAlign="center" maxW="800px" mx="auto">
                    <VStack spacing={6}>
                        <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <Box>
                                <AlertTitle>應用程式發生錯誤</AlertTitle>
                                <AlertDescription>
                                    很抱歉，應用程式遇到了一個未預期的錯誤。我們的技術團隊已收到通知，正在處理此問題。
                                </AlertDescription>
                            </Box>
                        </Alert>

                        {this.state.errorId && (
                            <Text fontSize="sm" color="gray.500">
                                錯誤 ID: {this.state.errorId}
                            </Text>
                        )}

                        {isDevelopment && error && (
                            <Box p={4} bg="red.50" border="1px" borderColor="red.200" rounded="md" w="full">
                                <Text fontSize="sm" color="red.700" fontFamily="mono" textAlign="left">
                                    <Text fontWeight="bold" mb={2}>錯誤詳情 (開發模式):</Text>
                                    <Text mb={2}>錯誤訊息: {error.message}</Text>
                                    {error.stack && (
                                        <Box>
                                            <Text fontWeight="bold" mb={1}>堆疊追蹤:</Text>
                                            <Text fontSize="xs" whiteSpace="pre-wrap">{error.stack}</Text>
                                        </Box>
                                    )}
                                </Text>
                            </Box>
                        )}

                        <VStack spacing={3}>
                            <Button
                                colorScheme="blue"
                                onClick={() => window.location.reload()}
                                size="lg"
                            >
                                重新整理頁面
                            </Button>

                            <Button
                                variant="outline"
                                onClick={() => this.setState({ hasError: false, error: undefined, errorId: undefined })}
                                size="md"
                            >
                                重試
                            </Button>
                        </VStack>

                        <Text fontSize="sm" color="gray.600" textAlign="center">
                            如果問題持續發生，請聯繫技術支援團隊並提供錯誤 ID。
                        </Text>
                    </VStack>
                </Box>
            )
        }

        return this.props.children
    }
}